import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowPathIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import PermissionsContainer, { useResolvedAccessLevel } from '../../components/PermissionsContainer';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import FilterBar from '../../components/FilterBar';
import SingleSelect from '../../components/SingleSelect';
import safetySystemService from '../../services/safetySystem.service';
import type { TicketRow, TicketSearchBy, TicketViolationRow, TicketsListParams, TicketsView } from '../../types/safetySystem';
import TicketDrawer from './components/TicketDrawer';
import {
  TICKET_COLUMNS, TICKET_DEFAULT_HIDDEN, TICKETS_DATE_COLUMNS, VIOLATION_COLUMNS, VIOLATION_DEFAULT_HIDDEN,
} from './SafetyTickets.columns';

/**
 * Tickets grid — port of the V1 Safety "Tickets" search with Ticket / Violations views.
 * Filter state lives in the URL (view, open, q, search_by, include_deleted) so the Hub can
 * deep-link (`/safety/tickets?open=1`); `open=<recno>` (any value other than 1) opens that
 * ticket's drawer instead.
 */

const VIEW_OPTIONS: { value: TicketsView; label: string }[] = [
  { value: 'ticket', label: 'Ticket view' },
  { value: 'tv', label: 'Violations view' },
];
const SEARCH_BY_OPTIONS: { value: TicketSearchBy; label: string }[] = [
  { value: 'ticketnum', label: 'Ticket #' },
  { value: 'unitnumber', label: 'Unit #' },
  { value: 'drivername', label: 'Driver name' },
  { value: 'incidentrecno', label: 'Incident #' },
];
const SEARCH_BYS = new Set<string>(SEARCH_BY_OPTIONS.map((o) => o.value));

const asView = (v: string | null): TicketsView => (v === 'tv' ? 'tv' : 'ticket');
const asSearchBy = (v: string | null): TicketSearchBy => (v && SEARCH_BYS.has(v) ? (v as TicketSearchBy) : 'ticketnum');

type Row = TicketRow | TicketViolationRow;
const rowId = (r: Row) => String('tvrecno' in r && r.tvrecno ? r.tvrecno : r.ticketrecno);

export default function SafetyTickets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageComponents } = usePermissionsMode();
  const canEdit = useResolvedAccessLevel('safety-system-unit-edit', true) >= 2;

  const pageComponentsList = useMemo(() => [
    { id: 'safety-system-tickets-table', name: 'Safety Tickets Table' },
    { id: 'safety-system-unit-edit', name: 'Unit Edit' },
  ], []);
  useEffect(() => {
    setPageComponents(pageComponentsList);
    return () => setPageComponents([]);
  }, [pageComponentsList, setPageComponents]);

  // ---- URL state -----------------------------------------------------------
  const view = asView(searchParams.get('view'));
  const openRaw = searchParams.get('open') || '';
  const openOnly = openRaw === '1';
  const openRecno = /^\d+$/.test(openRaw) && openRaw !== '1' ? Number(openRaw) : null;
  const q = searchParams.get('q') || '';
  const searchBy = asSearchBy(searchParams.get('search_by'));
  const includeDeleted = searchParams.get('include_deleted') === '1';

  const setParams = useCallback((patch: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [draft, setDraft] = useState(q);
  useEffect(() => { setDraft(q); }, [q]);
  const commitSearch = () => setParams({ q: draft.trim() });

  // ---- Data ----------------------------------------------------------------
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  const listParams = useMemo<TicketsListParams>(() => ({
    view, open: openOnly, q, search_by: searchBy, include_deleted: includeDeleted,
  }), [view, openOnly, q, searchBy, includeDeleted]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    safetySystemService.listTickets(listParams, controller.signal)
      .then((res) => { if (!controller.signal.aborted) setRows((res.data ?? []) as Row[]); })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setRows([]);
        setError(e?.response?.data?.detail || 'Unable to load tickets. Retry or check your access with an administrator.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [listParams, reloadTick]);

  // ---- Drawer --------------------------------------------------------------
  // `drawer` = closed | { recno: number | null } (null = New Ticket).
  const [drawer, setDrawer] = useState<{ recno: number | null } | null>(null);
  useEffect(() => {
    if (openRecno !== null) setDrawer({ recno: openRecno });
  }, [openRecno]);

  const closeDrawer = () => {
    setDrawer(null);
    if (openRecno !== null) setParams({ open: null });
  };

  // Both column sets are typed to their own row; the table is rendered over the union.
  const columns = (view === 'tv' ? VIOLATION_COLUMNS : TICKET_COLUMNS) as ColumnDef<Row>[];
  const defaultHidden = view === 'tv' ? VIOLATION_DEFAULT_HIDDEN : TICKET_DEFAULT_HIDDEN;

  return (
    <main className="flex h-[calc(100vh-7rem)] min-h-[450px] flex-col gap-2 overflow-hidden p-2 sm:p-4 text-gray-900 dark:text-gray-100">
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Safety Tickets</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Roadside inspections and violations. Click a row to open the ticket.</p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={() => setDrawer({ recno: null })}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg hover:bg-accent-hover"
            >
              <PlusIcon width={16} aria-hidden="true" />
              New Ticket
            </button>
          )}
          <button
            type="button"
            onClick={() => setReloadTick((t) => t + 1)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled"
          >
            <ArrowPathIcon width={16} aria-hidden="true" />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </header>
      {error && <div role="alert" className="shrink-0 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{error}</div>}

      <FilterBar wrap label="" className="shrink-0">
        <FilterBar.Group label="View">
          <SingleSelect ariaLabel="View" value={view} onChange={(v) => setParams({ view: v })}
            options={VIEW_OPTIONS} size="sm" className="w-40" usePortal />
        </FilterBar.Group>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" aria-label="Open only" checked={openOnly}
            onChange={(e) => setParams({ open: e.target.checked ? '1' : null })}
            className="h-4 w-4 rounded border-rule-strong" />
          Open only
        </label>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" aria-label="Include deleted" checked={includeDeleted}
            onChange={(e) => setParams({ include_deleted: e.target.checked ? '1' : null })}
            className="h-4 w-4 rounded border-rule-strong" />
          Include deleted
        </label>
        <FilterBar.Divider />
        <FilterBar.Group label="Search by">
          <SingleSelect ariaLabel="Search by" value={searchBy} onChange={(v) => setParams({ search_by: v })}
            options={SEARCH_BY_OPTIONS} size="sm" className="w-40" usePortal />
        </FilterBar.Group>
        <input
          type="text"
          aria-label="Search value"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commitSearch(); }}
          placeholder="Search…"
          className="h-8 w-48 rounded-md border border-rule-strong bg-field px-2 text-sm"
        />
        <button type="button" onClick={commitSearch}
          className="inline-flex h-8 items-center gap-1 rounded-md bg-accent px-3 text-sm font-medium text-accent-fg hover:bg-accent-hover">
          <MagnifyingGlassIcon width={16} aria-hidden="true" />Search
        </button>
        {q && (
          <button type="button" onClick={() => setParams({ q: null })}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-rule px-2 text-sm hover:bg-highlight">
            <XMarkIcon width={14} aria-hidden="true" />Clear search
          </button>
        )}
      </FilterBar>

      <PermissionsContainer componentId="safety-system-tickets-table" componentName="Safety Tickets Table" defaultHidden className="min-h-0 flex-1">
        <TanTable<Row>
          data={rows}
          columns={columns}
          loading={loading}
          fillContainer
          cardTitle={view === 'tv' ? 'Tickets — Violations view' : 'Tickets — Ticket view'}
          settingsKey={`safety-tickets-${view}`}
          fieldName={`safety-tickets-${view}`}
          searchPlaceholder="Filter results…"
          enableColumnFilters
          enableExcelExport
          enableShowHideCols
          enableRowsPerPage
          enableZebraStriping
          defaultSorting={[{ id: 'ticketnum', desc: true }]}
          defaultColumnVisibility={defaultHidden}
          defaultPageSize={100}
          dateColumns={TICKETS_DATE_COLUMNS}
          onRowClick={(r) => { if (r.ticketrecno) setDrawer({ recno: Number(r.ticketrecno) }); }}
          getRowId={rowId}
        />
      </PermissionsContainer>

      <TicketDrawer
        isOpen={drawer !== null}
        onClose={closeDrawer}
        ticketrecno={drawer?.recno ?? null}
        onSaved={() => setReloadTick((t) => t + 1)}
      />
    </main>
  );
}
